import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import StatusBar from './StatusBar'; // Import the StatusBar component
import { format } from 'date-fns';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS

const PatentDetails = () => {
  const { id } = useParams(); // Patent ID
  const [patent, setPatent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {

    const fetchPatentDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/patents/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch patent details');
        }
        const data = await response.json();
        console.log("Patent Data:", data);
        setPatent(data);
      } catch (error) {
        console.error("Error fetching patent details:", error);
        setError("Failed to fetch patent details. Please try again later.");
      }
    };

    fetchPatentDetails();
  }, [id]);

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!patent) return <div className="alert alert-info">Loading patent details...</div>;

  // Function to format date
  const formatDate = (dateStr) => {
    console.log("Date String:", dateStr);
    if (!dateStr) return 'N/A'; // Return 'N/A' for empty strings

    // Attempt to parse the date string
    const date = new Date(dateStr);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      // If the date is invalid, try to parse it in a different way
      // Example: If the date is in 'MM/DD/YYYY' format, you can split and create a new date
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        // Assuming the format is MM/DD/YYYY
        const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const newDate = new Date(year, month, day);
        return isNaN(newDate.getTime()) ? 'Invalid Date' : format(newDate, 'MMM dd, yyyy');
      }
      return 'Invalid Date'; // Return 'Invalid Date' if parsing fails
    }

    return format(date, 'MMM dd, yyyy'); // Return formatted date
  };

  const getStatusColor = (status) => {
    return status === 'Granted' ? 'green' : 'orange'; // Example logic
  };

  // Prepare dates for the status bar
  const dates = {
    'Published Date': patent.publishedDate,
    'FER Submitted Date': patent.ferSubmittedDate,
    'Hearing Date': patent.hearingDate,
    'Granted Date': patent.grantedDate,
  };

  const canUpdatePatent = () => {
    const accessLevel = sessionStorage.getItem('access_level');
    const email = sessionStorage.getItem('email');
  
    // Only allow access if the user has access level 5 and the specific email
    return accessLevel == '7';
  };

  return (
    <div className="container mt-5">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary mb-4">
        <div className="container-fluid">
          <h1 className="navbar-brand">Patent Details</h1>
          <Link to="/" className="btn btn-light">Home</Link>
        </div>
      </nav>

      {/* Main container for patent details */}
      <div className="row" style={styles.patentDetailsContainer}>
        {/* Left-side patent details */}
        <div className="col-md-6" style={styles.patentInfoContainer}>
          <h2 style={styles.patentTitle}>{patent.title}</h2>
          <div style={styles.patentInfo}>
            <p><strong>Department:</strong> {patent.department}</p>
            <p><strong>Application Number:</strong> {patent.applicationNumber}</p>
            <p><strong>Academic Year:</strong> {patent.year}</p>
            <p><strong>Filing Date:</strong> {formatDate(patent.filingDate)}</p>
            <p><strong>Inventor(s):</strong> {patent.inventors ? patent.inventors.split(',').join(', ') : 'N/A'}</p>
            <p><strong>Other Inventor(s):</strong> {patent.otherInventors ? patent.otherInventors.split(',').join(', ') : 'N/A'}</p>
            <p><strong>Patent Number:</strong> {patent.patentNumber}</p>
            <p><strong>Technology Readiness Level:</strong> {patent.technologyReadinessLevel}</p>
            <p>
              <strong>Commercialized:</strong> {patent.commercialized === 'yes' ? 'Yes' : 'No'}
            </p>
          </div>
          {/* Edit Button */}
          {canUpdatePatent() && (
            <div>
              <Link to={`/edit-patent/${id}`} className="btn btn-warning mt-3">
                Edit Patent
              </Link>
            </div>
          )}
        </div>

        {/* Status section on the right */}
        <div className="col-md-6" style={styles.statusTimelineContainer}>
          {/* Status */}
          <div style={styles.patentStatus}>
            <h2>Status: <span style={{ color: getStatusColor(patent.status) }}>{patent.status}</span></h2>
          </div>

          {/* Status Bar for dates */}
          <StatusBar dates={dates} />

          {/* Commercialization Details */}
          {patent.commercialized === 'yes' && patent.commercialization && (
            <div style={styles.commercializationInfo}>
              <h3>Commercialization Details</h3>
              <p><strong>Start Date:</strong> {formatDate(patent.commercialization.startDate)}</p>
              <p><strong>Commercialization Percentage:</strong> {patent.commercialization.commercializationPercentage}%</p>
              <p><strong>Funds Generated:</strong> ${patent.commercialization.fundsGenerated}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  patentDetailsContainer: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '20px auto',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f0f0f0',
    borderRadius: '10px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  patentInfoContainer: {
    padding: '20px',
    background: 'white',
    borderRadius: '10px',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
  },
  patentTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '20px',
  },
  patentInfo: {
    fontSize: '18px',
    color: '#555',
    lineHeight: '1.8',
  },
  statusTimelineContainer: {
    padding: '20px',
  },
  patentStatus: {
    marginBottom: '20px',
    fontSize: '22px',
    fontWeight: 'bold',
  },
  commercializationInfo: {
    marginTop: '20px',
    padding: '15px',
    background: '#e9f7ef',
    borderRadius: '10px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
};

export default PatentDetails;